import styled from "styled-components";
import {
  Button,
  Dialog,
  DialogSurface,
  Spinner,
} from "@fluentui/react-components";
import React, { useContext, useImperativeHandle, useState } from "react";
import { ApplyButton } from "./ApplyButton";
import { AvailableFieldsPane } from "./AvailableFieldsPane";
import { ColumnOptionsContext } from "./ColumnOptions";
import { SelectedColumnsPane } from "./SelectedColumnsPane";
import { ViewDescription } from "./ViewDescription";
import { ViewDialog } from "./ViewDialog";
import { ViewSelect } from "./ViewSelect";
import { ViewToolbar } from "./ViewToolbar";

export type ViewDialogState = {
  isOpen: boolean;
  userAppSettingId: number | null;
};

const initStateViewDialog = {
  isOpen: false,
  userAppSettingId: null,
};

export const ColumnOptionsPanel = () => {
  const [viewDialog, setViewDialog] =
    useState<ViewDialogState>(initStateViewDialog);
  const {
    isReady,
    closePanel,
    renderAdditionalOptions,
    strings,
    additionalOptionsHeight,
    additionalOptionsStyle,
    columnOptionsRef,
    hasUnsavedChanges,
    id,
  } = useContext(ColumnOptionsContext);

  useImperativeHandle(columnOptionsRef, () => ({
    closeModal: () => {
      setViewDialog({
        isOpen: false,
        userAppSettingId: null,
      });
    },
  }));

  return (
    <Dialog
      open
      modalType="modal"
      // A click outside must not throw away the user's work; Escape closes like "Close".
      onOpenChange={(_, data) => {
        if (!data.open && data.type !== "backdropClick") closePanel();
      }}
    >
      <Surface
        aria-label={strings.headerLabel}
        data-testid="column-options-dialog"
        id={id ? `${id}-dialog` : undefined}
      >
        <TopBar>
          <Title>{strings.headerLabel}</Title>
          <ViewSelectWrapper>
            <ViewSelect />
          </ViewSelectWrapper>
          {hasUnsavedChanges && (
            <UnsavedBadge
              title={strings.unsavedChangesHint}
              data-testid="column-options-unsaved"
            >
              <Dot />
              {strings.unsavedChangesLabel}
            </UnsavedBadge>
          )}
          <ViewDescriptionWrapper>
            <ViewDescription />
          </ViewDescriptionWrapper>
        </TopBar>
        <ToolbarRow>
          <ViewToolbar setViewDialog={setViewDialog} />
        </ToolbarRow>
        <Body aria-busy={!isReady}>
          <Panes>
            <AvailableFieldsPane />
            <SelectedColumnsPane />
          </Panes>
          {renderAdditionalOptions && (
            <AdditionalOptionsContainer
              height={additionalOptionsHeight}
              style={additionalOptionsStyle?.container}
            >
              {renderAdditionalOptions()}
            </AdditionalOptionsContainer>
          )}
          {!isReady && (
            <SpinnerOverlay>
              <Spinner size="large" />
            </SpinnerOverlay>
          )}
        </Body>
        <Footer>
          <Button
            appearance="secondary"
            onClick={closePanel}
            data-testid="column-options-close"
            id={id ? `${id}-btn-close` : undefined}
          >
            {strings.closeButtonLabel}
          </Button>
          <ApplyButton />
        </Footer>
        {viewDialog.isOpen && (
          <ViewDialog
            userAppSettingId={viewDialog.userAppSettingId}
            close={() => setViewDialog(initStateViewDialog)}
          />
        )}
      </Surface>
    </Dialog>
  );
};

const Surface = styled(DialogSurface)`
  width: min(1400px, 92vw);
  max-width: 92vw;
  height: 88vh;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px 4px 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
`;

const ViewSelectWrapper = styled.div`
  flex: 0 0 280px;
  min-width: 0;
`;

const UnsavedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(props) => props.theme.palette.themeDarker};
  background-color: ${(props) => props.theme.palette.themeLighter};
  border: 1px solid ${(props) => props.theme.palette.themeLight};
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.palette.themePrimary};
`;

const ViewDescriptionWrapper = styled.div`
  min-width: 0;
`;

const ToolbarRow = styled.div`
  padding: 4px 12px;
  border-bottom: 1px solid ${(props) => props.theme.palette.neutralLight};
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.palette.white};
  opacity: 0.7;
`;

const Body = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 12px 16px;
`;

const Panes = styled.div`
  display: flex;
  gap: 12px;
  flex: 1;
  min-height: 0;
`;

const AdditionalOptionsContainer = styled.div<{ height?: number }>`
  height: ${(props) => props.height || 80}px;
  padding: 0 16px;
  overflow-y: auto;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${(props) => props.theme.palette.neutralLight};
`;
